import { ixT } from '../i18n';
import type { CommandDef, CommandContribution, Localized, Scope, ScopeKind } from './types';

/** Declare a scope. `Ctx` types what active instances provide to the scope's commands. */
export function defineScope<Ctx = void>(def: {
  id: string;
  kind: ScopeKind;
  title: Localized;
  within?: readonly string[];
}): Scope<Ctx> {
  return Object.freeze({ ...def });
}

/** Declare a command (typed against its scope's context). */
export function defineCommand<Ctx>(def: CommandDef<Ctx>): CommandDef<Ctx> {
  return Object.freeze({ ...def });
}

/** The default export of a contribution file (`features/**\/commands.ts`). */
export function defineCommands(contribution: CommandContribution): CommandContribution {
  return contribution;
}

/** The one global scope (always active; ⌘K, `?`, `g` then a letter). */
export const globalScope = defineScope<void>({
  id: 'global',
  kind: 'global',
  title: () => ixT('ix.scope.global'),
});

/**
 * What an editor scope offers to the standard editing commands. Leave out what the editor does not
 * support; the matching command is then not registered for that scope.
 */
export type EditActions = {
  undo?: () => void;
  redo?: () => void;
  canUndo?: () => boolean;
  canRedo?: () => boolean;
  /** ⌘D in canvas, dashboard, workflow and interface editors (duplicate the selection). */
  duplicate?: () => void;
  selectAll?: () => void;
  /** Delete / Backspace on the selection (never while typing). */
  deleteSelection?: () => void;
  /** Esc: leave text edit, then deselect; returns false when there was nothing to leave. */
  escape?: () => boolean;
  /** Arrows (⇧ = 10). */
  nudge?: (dx: number, dy: number) => void;
  hasSelection?: () => boolean;
};

type Feature = 'undo' | 'redo' | 'duplicate' | 'selectAll' | 'delete' | 'escape' | 'nudge';

/**
 * The standard editor verbs of §4.2 (⌘Z, ⌘⇧Z and Ctrl+Y on Windows, ⌘D, ⌘A, Delete, Esc, arrows with
 * ⇧ = 10) for one editor scope. Clipboard keys stay native events: use `useClipboardTarget` /
 * `useClipboardSource`, which receive the browser's copy, cut and paste events.
 */
export function editCommands<Ctx extends EditActions>(
  scope: Scope<Ctx>,
  features: readonly Feature[] = ['undo', 'redo', 'duplicate', 'selectAll', 'delete', 'escape', 'nudge'],
): CommandDef<Ctx>[] {
  const has = (f: Feature) => features.includes(f);
  const id = (name: string) => `${scope.id}.${name}`;
  const selected = (ctx: Ctx) => (ctx.hasSelection ? ctx.hasSelection() : true);
  const out: CommandDef<Ctx>[] = [];
  if (has('undo'))
    out.push(
      defineCommand<Ctx>({
        id: id('undo'),
        title: () => ixT('ix.cmd.undo'),
        shortcut: 'mod+z',
        scope,
        repeat: true,
        when: (ctx) => Boolean(ctx.undo) && (ctx.canUndo ? ctx.canUndo() : true),
        run: (ctx) => ctx.undo?.(),
      }),
    );
  if (has('redo'))
    out.push(
      defineCommand<Ctx>({
        id: id('redo'),
        title: () => ixT('ix.cmd.redo'),
        shortcut: ['mod+shift+z', { windows: 'ctrl+y' }],
        scope,
        repeat: true,
        when: (ctx) => Boolean(ctx.redo) && (ctx.canRedo ? ctx.canRedo() : true),
        run: (ctx) => ctx.redo?.(),
      }),
    );
  if (has('duplicate'))
    out.push(
      defineCommand<Ctx>({
        id: id('duplicate'),
        title: () => ixT('ix.cmd.duplicate'),
        icon: 'duplicate',
        shortcut: 'mod+d',
        scope,
        when: (ctx) => Boolean(ctx.duplicate) && selected(ctx),
        run: (ctx) => ctx.duplicate?.(),
      }),
    );
  if (has('selectAll'))
    out.push(
      defineCommand<Ctx>({
        id: id('selectAll'),
        title: () => ixT('ix.cmd.selectAll'),
        shortcut: 'mod+a',
        scope,
        when: (ctx) => Boolean(ctx.selectAll),
        run: (ctx) => ctx.selectAll?.(),
      }),
    );
  if (has('delete'))
    out.push(
      defineCommand<Ctx>({
        id: id('delete'),
        title: () => ixT('ix.cmd.delete'),
        icon: 'trash',
        shortcut: ['delete', 'backspace'],
        scope,
        when: (ctx) => Boolean(ctx.deleteSelection) && selected(ctx),
        run: (ctx) => ctx.deleteSelection?.(),
      }),
    );
  if (has('escape'))
    out.push(
      defineCommand<Ctx>({
        id: id('escape'),
        title: () => ixT('ix.cmd.escape'),
        shortcut: 'escape',
        scope,
        when: (ctx) => Boolean(ctx.escape),
        run: (ctx) => ctx.escape?.() ?? false,
      }),
    );
  if (has('nudge')) {
    const nudges: Array<[string, Parameters<typeof ixT>[0], number, number, boolean]> = [
      ['nudgeUp', 'ix.cmd.nudgeUp', 0, -1, false],
      ['nudgeDown', 'ix.cmd.nudgeDown', 0, 1, false],
      ['nudgeLeft', 'ix.cmd.nudgeLeft', -1, 0, false],
      ['nudgeRight', 'ix.cmd.nudgeRight', 1, 0, false],
      ['nudgeUpFar', 'ix.cmd.nudgeUpFar', 0, -10, true],
      ['nudgeDownFar', 'ix.cmd.nudgeDownFar', 0, 10, true],
      ['nudgeLeftFar', 'ix.cmd.nudgeLeftFar', -10, 0, true],
      ['nudgeRightFar', 'ix.cmd.nudgeRightFar', 10, 0, true],
    ];
    for (const [name, title, dx, dy, far] of nudges) {
      const arrow = dx < 0 ? 'arrowleft' : dx > 0 ? 'arrowright' : dy < 0 ? 'arrowup' : 'arrowdown';
      out.push(
        defineCommand<Ctx>({
          id: id(name),
          title: () => ixT(title),
          shortcut: far ? `shift+${arrow}` : arrow,
          scope,
          repeat: true,
          hidden: far,
          when: (ctx) => Boolean(ctx.nudge) && selected(ctx),
          run: (ctx) => ctx.nudge?.(dx, dy),
        }),
      );
    }
  }
  return out;
}
